import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({ isOpen, onClose, onSelect, initialDate = '', minDate = '' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (initialDate) {
      setSelectedDate(new Date(initialDate));
      setCurrentDate(new Date(initialDate));
    } else {
      setSelectedDate(new Date());
    }
  }, [initialDate]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1)?.getDay();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    const daysInPrevMonth = new Date(year, month, 0)?.getDate();

    const days = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      days?.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, daysInPrevMonth - i)
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days?.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    // Next month days
    const remainingDays = 42 - days?.length;
    for (let i = 1; i <= remainingDays; i++) {
      days?.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() + 1, 1));
  };

  const handleDateClick = (dateObj) => {
    const minDateObj = minDate ? new Date(minDate) : null;
    if (minDateObj && dateObj?.date < minDateObj) {
      return;
    }
    setSelectedDate(dateObj?.date);
  };

  const handleConfirm = () => {
    if (selectedDate) {
      const year = selectedDate?.getFullYear();
      const month = String(selectedDate?.getMonth() + 1)?.padStart(2, '0');
      const day = String(selectedDate?.getDate())?.padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      onSelect?.(formattedDate);
      onClose?.();
    }
  };

  const isDateDisabled = (dateObj) => {
    if (!minDate) return false;
    const minDateObj = new Date(minDate);
    minDateObj?.setHours(0, 0, 0, 0);
    const checkDate = new Date(dateObj?.date);
    checkDate?.setHours(0, 0, 0, 0);
    return checkDate < minDateObj;
  };

  const isDateSelected = (dateObj) => {
    if (!selectedDate) return false;
    return (
      dateObj?.date?.getDate() === selectedDate?.getDate() &&
      dateObj?.date?.getMonth() === selectedDate?.getMonth() &&
      dateObj?.date?.getFullYear() === selectedDate?.getFullYear()
    );
  };

  const isToday = (dateObj) => {
    const today = new Date();
    return (
      dateObj?.date?.getDate() === today?.getDate() &&
      dateObj?.date?.getMonth() === today?.getMonth() &&
      dateObj?.date?.getFullYear() === today?.getFullYear()
    );
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentDate);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[380px] overflow-hidden" onClick={(e) => e?.stopPropagation()}>
        {/* Header with Month/Year Display */}
        <div className="bg-blue-500 text-white p-6">
          <div className="text-sm font-medium opacity-80 mb-1">
            {selectedDate?.getFullYear()}
          </div>
          <div className="text-3xl font-light">
            {selectedDate ? `${daysOfWeek?.[selectedDate?.getDay()]}, ${monthNames?.[selectedDate?.getMonth()]} ${selectedDate?.getDate()}` : 'Select Date'}
          </div>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="text-lg font-semibold text-gray-800">
            {monthNames?.[currentDate?.getMonth()]} {currentDate?.getFullYear()}
          </div>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4 bg-white">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {daysOfWeek?.map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Date Grid */}
          <div className="grid grid-cols-7 gap-2">
            {days?.map((dateObj, index) => {
              const disabled = isDateDisabled(dateObj);
              const selected = isDateSelected(dateObj);
              const today = isToday(dateObj);

              return (
                <button
                  key={index}
                  onClick={() => !disabled && handleDateClick(dateObj)}
                  disabled={disabled}
                  className={`
                    aspect-square flex items-center justify-center rounded-full text-sm font-medium transition-all
                    ${
                      selected
                        ? 'bg-blue-500 text-white shadow-lg scale-110'
                        : disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : dateObj?.isCurrentMonth
                        ? 'text-gray-800 hover:bg-blue-100' :'text-gray-400 hover:bg-gray-100'
                    }
                    ${
                      today && !selected
                        ? 'border-2 border-blue-500' :''
                    }
                  `}
                >
                  {dateObj?.day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 p-4 bg-white border-t">
          <button
            onClick={onClose}
            className="text-blue-500 font-semibold px-6 py-2 hover:bg-blue-50 rounded transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={handleConfirm}
            className="text-blue-500 font-semibold px-6 py-2 hover:bg-blue-50 rounded transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;